import {
  useEntityOverTimeContext,
  useTeamTree,
  useGroupContext,
} from '@internal/backstage-plugin-common-library-react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { LinkButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import {
  Box,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Card,
  Typography,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { useGetCategoriesData, useGetCategories } from '../../hooks';
import { getScorecardItems, useScorecardLinkBuilder } from '../../utils';
import { getAggregatedScorecardItems } from '../../utils/scorecardHierarchyUtils';
import { ScorecardProgress } from '../../widgets/ScorecardProgress';
import { SCORECARD_HIERARCHY } from '../ScorecardCategories/scorecardHierarchy';
import { TeamScorecardSummary } from '../TeamScorecardSummary';

const useStyles = makeStyles(theme => ({
  navContainer: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  navButton: {
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',

    '&:hover:not(:disabled)': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[2],
    },

    '&:disabled': {
      opacity: 0.5,
    },
  },
  menuPaper: {
    minWidth: 200,
    marginTop: theme.spacing(0.5),
    boxShadow: theme.shadows[8],
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    transition: 'background-color 0.15s ease',

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardRoot: {
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    overflow: 'visible',
    transition: 'box-shadow 0.3s ease',
  },
  topSection: {
    padding: theme.spacing(4),
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
  },
  navWrapper: {
    position: 'absolute',
    top: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 10,

    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
      right: 0,
      marginBottom: theme.spacing(3),
    },
  },
  divider: {
    margin: theme.spacing(0, 4),

    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(0, 3),
    },
  },
  bottomSection: {
    padding: theme.spacing(4),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  childTeamBadge: {
    marginLeft: theme.spacing(1),
    height: '20px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
}));

const TeamNavigationButtons = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const { isParentGroup, childTeams } = useGroupContext();
  const buildLink = useScorecardLinkBuilder();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const parentTeam = useMemo(() => {
    if (!entity || entity.kind !== 'Group') return null;
    const rel = entity.relations?.find(
      r => r.type === 'childOf' && r.targetRef.startsWith('group:'),
    );
    return rel ? rel.targetRef.split(':')[1].split('/')[1] : null;
  }, [entity]);

  const childTeamsList = useMemo(() => {
    return isParentGroup && Array.isArray(childTeams)
      ? childTeams.slice(1)
      : [];
  }, [isParentGroup, childTeams]);

  if (!entity || entity.kind !== 'Group') return null;

  return (
    <Box className={classes.navContainer}>
      {parentTeam && (
        <Tooltip title={`Navigate to ${parentTeam} team scorecard`}>
          <LinkButton
            variant="outlined"
            color="primary"
            className={classes.navButton}
            component="a"
            href={buildLink(`/catalog/default/group/${parentTeam}/scorecards`)}
            startIcon={<ChevronUp size={16} />}
            to=""
          >
            View Parent Team
          </LinkButton>
        </Tooltip>
      )}

      {childTeamsList.length > 0 && (
        <Tooltip title="View scorecards for child teams">
          <LinkButton
            variant="outlined"
            color="primary"
            className={classes.navButton}
            onClick={e => setAnchorEl(e.currentTarget)}
            startIcon={
              <ChevronDown
                size={16}
                style={{
                  transform: Boolean(anchorEl)
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            }
            to=""
          >
            View Child Teams
            <Chip
              label={childTeamsList.length}
              size="small"
              color="primary"
              className={classes.childTeamBadge}
            />
          </LinkButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {childTeamsList.map(team => (
          <MenuItem
            key={team.id}
            component="a"
            href={buildLink(`/catalog/default/group/${team.id}/scorecards`)}
            onClick={() => setAnchorEl(null)}
            className={classes.menuItem}
          >
            <Typography variant="body2">{team.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const TeamScoreCard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { entity } = useEntity();
  const { teamTree } = useTeamTree(entity.metadata.name);
  const teamTreeData = useMemo(() => teamTree?.tree, [teamTree?.tree]);
  const timeContext = useEntityOverTimeContext();

  const { value: categories, loading: categoriesLoading } = useGetCategories();

  const { loading, value: categoriesData } = useGetCategoriesData(
    teamTreeData,
    timeContext.dateRange?.start,
    timeContext.dateRange?.end,
    timeContext.aggregateBy,
  );

  const { flatScorecardItems, groupedScorecardItems } = useMemo(() => {
    if (!categoriesData)
      return { flatScorecardItems: null, groupedScorecardItems: null };

    const rawItems = getScorecardItems(categoriesData, timeContext.aggregateBy);
    const aggregatedItems = getAggregatedScorecardItems(
      rawItems,
      SCORECARD_HIERARCHY,
    );

    return {
      flatScorecardItems: rawItems,
      groupedScorecardItems: aggregatedItems,
    };
  }, [categoriesData, timeContext.aggregateBy]);

  if (loading || categoriesLoading) {
    return (
      <Card className={classes.cardRoot}>
        <Box className={classes.loadingContainer}>
          <Box textAlign="center">
            <CircularProgress size={48} />
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 16 }}
            >
              Loading scorecard data...
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }

  if (!groupedScorecardItems || groupedScorecardItems.length === 0) {
    return (
      <Card className={classes.cardRoot}>
        <Box className={classes.emptyState}>
          <Typography variant="h6" gutterBottom>
            No Scorecard Data Available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            There is no scorecard data for the selected time period.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      className={classes.cardRoot}
      style={{
        boxShadow:
          theme.palette.type === 'dark'
            ? '0 4px 20px rgba(0,0,0,0.4)'
            : '0 2px 12px rgba(0,0,0,0.08)',
      }}
    >
      <Box className={classes.topSection}>
        <Box className={classes.navWrapper}>
          <TeamNavigationButtons />
        </Box>
        <ScorecardProgress
          scorecardItems={groupedScorecardItems}
          flatScorecardItems={flatScorecardItems}
        />
      </Box>

      <Divider className={classes.divider} />

      <Box className={classes.bottomSection}>
        <TeamScorecardSummary
          categories={categories}
          categoriesData={categoriesData}
          entity={entity}
          teamTreeData={teamTreeData}
        />
      </Box>
    </Card>
  );
};
